import { buildApiUrl } from '../config/api';

const SESSION_KEY = 'abraliber_analytics_session';
const MAX_BATCH_SIZE = 25;
const FLUSH_DELAY = 3000;

let eventQueue = [];
let flushTimer = null;
const recentSearches = new Map();

const createSessionId = () => {
  if (window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }

  return `${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 14)}`;
};

const getSessionId = () => {
  let sessionId = sessionStorage.getItem(SESSION_KEY);
  if (!sessionId) {
    sessionId = createSessionId();
    sessionStorage.setItem(SESSION_KEY, sessionId);
  }
  return sessionId;
};

const currentPath = () => window.location.pathname || '/';

const trimValue = (value, maxLength) => {
  if (typeof value !== 'string') return value;
  return value.replace(/\s+/g, ' ').trim().slice(0, maxLength);
};

const sanitizeEvent = (event) => ({
  session_id: getSessionId(),
  event_type: event.event_type,
  page_path: trimValue(event.page_path || currentPath(), 255),
  course_id: event.course_id ? Number(event.course_id) : undefined,
  search_query: trimValue(event.search_query, 255),
  result_count: Number.isFinite(event.result_count) ? event.result_count : undefined,
  x_percent: Number.isFinite(event.x_percent) ? event.x_percent : undefined,
  y_percent: Number.isFinite(event.y_percent) ? event.y_percent : undefined,
  viewport_width: event.viewport_width,
  viewport_height: event.viewport_height,
  document_height: event.document_height,
  element_tag: trimValue(event.element_tag, 32),
  element_label: trimValue(event.element_label, 160),
  element_href: trimValue(event.element_href, 255),
  source: trimValue(event.source, 64),
});

const scheduleFlush = () => {
  if (flushTimer) return;
  flushTimer = window.setTimeout(() => {
    flushTimer = null;
    flush();
  }, FLUSH_DELAY);
};

const flush = () => {
  if (eventQueue.length === 0) return;

  const events = eventQueue.splice(0, MAX_BATCH_SIZE);
  const token = localStorage.getItem('token');
  const headers = {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  };

  if (token) headers.Authorization = `Bearer ${token}`;

  fetch(buildApiUrl('site-events'), {
    method: 'POST',
    headers,
    body: JSON.stringify({ events }),
    keepalive: true,
    credentials: 'same-origin',
  }).catch(() => {});

  if (eventQueue.length > 0) scheduleFlush();
};

const track = (event) => {
  if (!event?.event_type || currentPath().startsWith('/admin')) return;
  eventQueue.push(sanitizeEvent(event));

  if (eventQueue.length >= MAX_BATCH_SIZE) {
    flush();
  } else {
    scheduleFlush();
  }
};

const trackPageView = (pagePath) => {
  track({ event_type: 'page_view', page_path: pagePath });
};

const trackSearch = (query, resultCount, source) => {
  const normalized = trimValue(query, 255).toLocaleLowerCase('pt-BR');
  if (normalized.length < 2) return;

  const dedupeKey = `${source}:${normalized}`;
  const lastTrackedAt = recentSearches.get(dedupeKey) || 0;
  if (Date.now() - lastTrackedAt < 10000) return;
  recentSearches.set(dedupeKey, Date.now());

  track({
    event_type: 'search',
    search_query: query,
    result_count: resultCount,
    source,
  });
};

const trackClientError = ({ status, endpoint, method, message }) => {
  const normalizedStatus = Number(status) || 0;
  const safeEndpoint = trimValue(String(endpoint || 'requisição desconhecida').split('?')[0], 100);
  const safeMethod = trimValue(String(method || 'GET').toUpperCase(), 10);
  const safeMessage = trimValue(String(message || 'Falha de comunicação com o servidor'), 120);

  track({
    event_type: 'client_error',
    element_label: `${safeMethod} ${safeEndpoint}: ${safeMessage}`,
    source: normalizedStatus ? `HTTP ${normalizedStatus}` : 'REDE',
  });
};

const analyticsService = {
  track,
  trackPageView,
  trackSearch,
  trackClientError,
  flush,
};

export default analyticsService;
